import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, Activity, Eye, Search, Globe, Server, Cpu, 
  Lock, Unlock, FileWarning, Zap, TrendingUp, TrendingDown, Brain,
  Target, AlertCircle, CheckCircle, Clock, Users, Loader2, Plus,
  ChevronRight, ExternalLink, RefreshCw, Settings, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityColors = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/30' },
  high: { bg: 'bg-orange-500/10', text: 'text-orange-500', border: 'border-orange-500/30' },
  medium: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/30' },
  low: { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/30' },
};

export default function SecurityCenterPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [healthScore, setHealthScore] = useState(null);
  const [aiDashboard, setAiDashboard] = useState(null);
  const [darkwebAlerts, setDarkwebAlerts] = useState([]);
  const [socEvents, setSocEvents] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [threatModels, setThreatModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [showThreatModelForm, setShowThreatModelForm] = useState(false);
  const [showConfigureAPIs, setShowConfigureAPIs] = useState(false);
  const [apiConfig, setApiConfig] = useState({
    darkweb_api_url: '',
    darkweb_api_key: '',
    siem_api_url: '',
    siem_api_key: '',
    threat_intel_api_url: '',
    threat_intel_api_key: '',
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [dwTargets, setDwTargets] = useState('');
  const [dwStatus, setDwStatus] = useState({ targets: [] });
  const [socMetrics, setSocMetrics] = useState(null);
  const [threatIntel, setThreatIntel] = useState(null);
  
  const [incidentForm, setIncidentForm] = useState({ title: '', description: '', severity: 'medium', category: 'security_event', affected_assets: '' });
  const [threatModelForm, setThreatModelForm] = useState({ application_name: '', application_type: 'web', assets: '', data_flows: '' });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [healthRes, aiRes, darkwebRes, socRes, incidentsRes, modelsRes, dwStatusRes, metricsRes, intelRes] = await Promise.all([
        axios.get(`${API}/security/health-score`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/security/dashboard-ai`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/security/darkweb/alerts`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/security/soc/events`, { withCredentials: true }).catch(() => ({ data: { events: [] } })),
        axios.get(`${API}/security/incidents`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/security/threat-model/list`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/security/darkweb/status`, { withCredentials: true }).catch(() => ({ data: { targets: [] } })),
        axios.get(`${API}/security/soc/metrics`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/security/soc/threat-intel`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      
      setHealthScore(healthRes.data);
      setAiDashboard(aiRes.data);
      setDarkwebAlerts(darkwebRes.data);
      setSocEvents(socRes.data.events || []);
      setIncidents(incidentsRes.data);
      setThreatModels(modelsRes.data);
      setDwStatus(dwStatusRes.data);
      setSocMetrics(metricsRes.data);
      setThreatIntel(intelRes.data);
    } catch (err) {
      console.error('Error fetching security data:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveApiConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API}/security/config`, apiConfig, { withCredentials: true });
      toast.success('API configuration saved');
      setShowConfigureAPIs(false);
      fetchAllData();
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const runDarkwebScan = async () => {
    const fresh = dwTargets.split(/[\s,]+/).map(t => t.trim()).filter(Boolean);
    const targets = fresh.length ? fresh : (dwStatus.targets || []).map(t => t.target);
    if (!targets.length) { toast.error('Enter a domain (e.g. yourcompany.com) to check for breaches'); return; }
    setScanning(true);
    try {
      const res = await axios.post(`${API}/security/darkweb/scan`, { targets, scan_type: 'credential_check' }, { withCredentials: true });
      toast.success(`Breach check complete: ${res.data.findings?.length || 0} exposure(s) across ${targets.length} target(s)`);
      if (res.data.message) toast.info(res.data.message);
      setDwTargets('');
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const removeDwTarget = async (t) => {
    try { await axios.delete(`${API}/security/darkweb/targets/${encodeURIComponent(t)}`, { withCredentials: true }); fetchAllData(); } catch { toast.error('Could not remove target'); }
  };

  const createIncident = async () => {
    try {
      const res = await axios.post(`${API}/security/incidents`, {
        ...incidentForm,
        affected_assets: incidentForm.affected_assets.split(',').map(a => a.trim()).filter(Boolean)
      }, { withCredentials: true });
      setIncidents(prev => [res.data, ...prev]);
      setShowIncidentForm(false);
      setIncidentForm({ title: '', description: '', severity: 'medium', category: 'security_event', affected_assets: '' });
      toast.success('Incident created');
    } catch (err) {
      toast.error('Failed to create incident');
    }
  };

  const createThreatModel = async () => {
    try {
      const res = await axios.post(`${API}/security/threat-model/analyze`, {
        ...threatModelForm,
        assets: threatModelForm.assets.split(',').map(a => a.trim()).filter(Boolean),
        data_flows: threatModelForm.data_flows.split(',').map(d => d.trim()).filter(Boolean)
      }, { withCredentials: true });
      setThreatModels(prev => [res.data, ...prev]);
      setShowThreatModelForm(false);
      setThreatModelForm({ application_name: '', application_type: 'web', assets: '', data_flows: '' });
      toast.success('Threat model created');
    } catch (err) {
      toast.error('Failed to create threat model');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-primary mx-auto mb-3 animate-spin" />
          <p className="text-sm text-muted-foreground">Loading security center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-center-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight" data-testid="security-center-title">
            Security Command Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comprehensive security monitoring, threat intelligence, and incident response
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAllData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Dialog open={showConfigureAPIs} onOpenChange={setShowConfigureAPIs}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="settings-btn">
                <Settings className="h-4 w-4 mr-1" /> Configure APIs
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Configure Security API Integrations</DialogTitle>
              </DialogHeader>
              <ScrollArea className="max-h-[60vh]">
                <div className="space-y-6 pt-4 pr-4">
                  {/* Darkweb API */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-purple-500" />
                      <h4 className="font-medium text-sm">Darkweb Monitoring</h4>
                    </div>
                    <p className="text-xs text-muted-foreground">Connect to SpyCloud, Have I Been Pwned, or similar providers</p>
                    <div className="grid gap-3">
                      <div>
                        <Label className="text-xs">API URL</Label>
                        <Input 
                          value={apiConfig.darkweb_api_url} 
                          onChange={e => setApiConfig(p => ({ ...p, darkweb_api_url: e.target.value }))}
                          placeholder="https://api.darkweb-provider.com/v1"
                          className="text-sm"
                          data-testid="darkweb-api-url"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">API Key</Label>
                        <Input 
                          type="password"
                          value={apiConfig.darkweb_api_key} 
                          onChange={e => setApiConfig(p => ({ ...p, darkweb_api_key: e.target.value }))}
                          placeholder="Enter API key"
                          className="text-sm"
                          data-testid="darkweb-api-key"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* SIEM API */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-blue-500" />
                      <h4 className="font-medium text-sm">SIEM / SOC Integration</h4>
                    </div>
                    <p className="text-xs text-muted-foreground">Connect to Splunk, Elastic SIEM, QRadar, or Microsoft Sentinel</p>
                    <div className="grid gap-3">
                      <div>
                        <Label className="text-xs">SIEM URL</Label>
                        <Input 
                          value={apiConfig.siem_api_url} 
                          onChange={e => setApiConfig(p => ({ ...p, siem_api_url: e.target.value }))}
                          placeholder="https://your-siem.com/api"
                          className="text-sm"
                          data-testid="siem-api-url"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">API Key</Label>
                        <Input 
                          type="password"
                          value={apiConfig.siem_api_key} 
                          onChange={e => setApiConfig(p => ({ ...p, siem_api_key: e.target.value }))}
                          placeholder="Enter API key"
                          className="text-sm"
                          data-testid="siem-api-key"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Threat Intel API */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-red-500" />
                      <h4 className="font-medium text-sm">Threat Intelligence</h4>
                    </div>
                    <p className="text-xs text-muted-foreground">Connect to VirusTotal, AlienVault OTX, or Shodan</p>
                    <div className="grid gap-3">
                      <div>
                        <Label className="text-xs">API URL</Label>
                        <Input 
                          value={apiConfig.threat_intel_api_url} 
                          onChange={e => setApiConfig(p => ({ ...p, threat_intel_api_url: e.target.value }))}
                          placeholder="https://threat-intel.com/api"
                          className="text-sm"
                          data-testid="threat-intel-api-url"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">API Key</Label>
                        <Input 
                          type="password"
                          value={apiConfig.threat_intel_api_key} 
                          onChange={e => setApiConfig(p => ({ ...p, threat_intel_api_key: e.target.value }))}
                          placeholder="Enter API key"
                          className="text-sm"
                          data-testid="threat-intel-api-key"
                        />
                      </div>
                    </div>
                  </div>

                  <Button onClick={saveApiConfig} disabled={savingConfig} className="w-full" data-testid="save-api-config-btn">
                    {savingConfig ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Configuration
                  </Button>
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl" data-testid="security-tabs">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="darkweb">Darkweb</TabsTrigger>
          <TabsTrigger value="soc">SOC</TabsTrigger>
          <TabsTrigger value="threats">Threat Model</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Health Score Card */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-1" data-testid="health-score-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" /> Security Health Score
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex flex-col items-center">
                  <div className="relative w-32 h-32 mb-4">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                      <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" className="stroke-muted" />
                      <circle 
                        cx="50" cy="50" r="42" fill="none" strokeWidth="8" 
                        stroke={healthScore?.overall_score >= 70 ? '#22c55e' : healthScore?.overall_score >= 40 ? '#eab308' : '#ef4444'}
                        strokeDasharray={`${(healthScore?.overall_score || 0) * 2.64} 264`} 
                        strokeLinecap="round" 
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-bold">{healthScore?.overall_score || 0}</span>
                      <span className="text-xs text-muted-foreground">/100</span>
                    </div>
                  </div>
                  <Badge variant="outline" className={`mb-3 ${
                    healthScore?.status === 'healthy' ? 'border-green-500/30 text-green-500' :
                    healthScore?.status === 'moderate' ? 'border-yellow-500/30 text-yellow-500' :
                    'border-red-500/30 text-red-500'
                  }`}>
                    {healthScore?.status?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                  <div className="w-full space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Vulnerability Score</span><span className="font-medium">{healthScore?.components?.vulnerability_score || 0}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Remediation Rate</span><span className="font-medium">{healthScore?.components?.remediation_rate || 0}%</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Compliance Score</span><span className="font-medium">{healthScore?.components?.compliance_score || 0}</span></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Insights */}
            <Card className="lg:col-span-2" data-testid="ai-insights-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Brain className="h-4 w-4 text-primary" /> AI Security Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[280px]">
                  <div className="space-y-3">
                    {aiDashboard?.ai_insights?.map((insight, i) => (
                      <div key={i} className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg shrink-0 ${
                            insight.severity === 'high' ? 'bg-orange-500/10' : 
                            insight.severity === 'medium' ? 'bg-yellow-500/10' : 'bg-green-500/10'
                          }`}>
                            <Zap className={`h-4 w-4 ${
                              insight.severity === 'high' ? 'text-orange-500' : 
                              insight.severity === 'medium' ? 'text-yellow-500' : 'text-green-500'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">{insight.title}</span>
                              <Badge variant="outline" className="text-[10px]">{Math.round(insight.confidence * 100)}% confidence</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{insight.insight}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card data-testid="stat-vulns">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{healthScore?.metrics?.open_vulnerabilities || 0}</p>
                <p className="text-xs text-muted-foreground">Open Vulnerabilities</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-critical">
              <CardContent className="p-4 text-center">
                <AlertCircle className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{healthScore?.metrics?.critical_open || 0}</p>
                <p className="text-xs text-muted-foreground">Critical Issues</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-incidents">
              <CardContent className="p-4 text-center">
                <FileWarning className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{incidents.length}</p>
                <p className="text-xs text-muted-foreground">Active Incidents</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-assets">
              <CardContent className="p-4 text-center">
                <Server className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{aiDashboard?.attack_surface?.total_assets || 0}</p>
                <p className="text-xs text-muted-foreground">Total Assets</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Darkweb Monitoring Tab */}
        <TabsContent value="darkweb" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Darkweb Monitoring</h2>
              <p className="text-sm text-muted-foreground">Monitor for credential leaks, brand mentions, and threat actor activity</p>
            </div>
            <Badge variant="outline" className="border-green-500/30 text-green-500 gap-1"><Globe className="h-3 w-3" /> Live · HaveIBeenPwned</Badge>
          </div>

          <Card data-testid="darkweb-targets-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input value={dwTargets} onChange={e => setDwTargets(e.target.value)} onKeyDown={e => e.key === 'Enter' && runDarkwebScan()} placeholder="yourcompany.com, brand.io (domains) — emails need HIBP_API_KEY" className="font-mono text-sm" data-testid="darkweb-target-input" />
                <Button onClick={runDarkwebScan} disabled={scanning} className="shrink-0" data-testid="darkweb-scan-btn">
                  {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  {dwTargets.trim() ? 'Check & monitor' : 'Re-check monitored'}
                </Button>
              </div>
              {dwStatus.targets?.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="darkweb-monitored-list">
                  {dwStatus.targets.map(t => (
                    <span key={t.target} className="inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded-full border bg-muted/40">
                      {t.target}
                      <button onClick={() => removeDwTarget(t.target)} className="text-muted-foreground hover:text-destructive" title="Stop monitoring" data-testid={`darkweb-remove-${t.target}`}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">Checks the public HaveIBeenPwned breach corpus for exposures tied to your domains. Findings are real breach records, not simulations.</p>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
              <CardContent className="p-4">
                <Globe className="h-8 w-8 text-purple-500 mb-2" />
                <p className="font-semibold">Credential Monitoring</p>
                <p className="text-xs text-muted-foreground">Track exposed credentials across darkweb forums and paste sites</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
              <CardContent className="p-4">
                <Eye className="h-8 w-8 text-blue-500 mb-2" />
                <p className="font-semibold">Brand Monitoring</p>
                <p className="text-xs text-muted-foreground">Monitor mentions of your brand in underground markets</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border-red-500/20">
              <CardContent className="p-4">
                <Target className="h-8 w-8 text-red-500 mb-2" />
                <p className="font-semibold">Threat Actor Tracking</p>
                <p className="text-xs text-muted-foreground">Track threat actors targeting your industry</p>
              </CardContent>
            </Card>
          </div>

          <Card data-testid="darkweb-alerts">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Recent Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {darkwebAlerts.map((alert, i) => (
                    <div key={alert.alert_id || i} className="p-3 rounded-lg border bg-card flex items-start gap-3">
                      <div className={`p-2 rounded-lg shrink-0 ${severityColors[alert.severity]?.bg || 'bg-muted'}`}>
                        <AlertTriangle className={`h-4 w-4 ${severityColors[alert.severity]?.text || 'text-muted-foreground'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{alert.title}</span>
                          <Badge variant="outline" className={`text-[10px] ${severityColors[alert.severity]?.border || ''} ${severityColors[alert.severity]?.text || ''}`}>
                            {alert.severity}
                          </Badge>
                          {alert.is_demo && <Badge variant="outline" className="text-[10px]">Demo</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">{alert.description}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{alert.breach_date ? `Breach date ${alert.breach_date} · ` : ''}Detected {new Date(alert.detected_at).toLocaleString()}{alert.source ? ` · ${alert.source}` : ''}</p>
                      </div>
                    </div>
                  ))}
                  {darkwebAlerts.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Globe className="h-10 w-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No exposures found yet. Add your domains above to start monitoring.</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SOC Tab */}
        <TabsContent value="soc" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Security Operations Center</h2>
              <p className="text-sm text-muted-foreground">Real-time security event monitoring and SIEM integration</p>
            </div>
            <Badge variant="outline" className="border-green-500/30 text-green-500">Live Monitoring</Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="soc-metrics">
            {socMetrics ? Object.entries(socMetrics.metrics).map(([k, m]) => (
              <Card key={k} data-testid={`soc-metric-${k}`}><CardContent className="p-4 text-center"><p className={`text-2xl font-heading font-bold ${['high_severity_24h', 'kev_matches', 'darkweb_alerts'].includes(k) && m.value ? 'text-severity-high' : 'text-primary'}`}>{m.value ?? '—'}{m.unit === 'minutes' && m.value != null ? 'm' : ''}</p><p className="text-[11px] text-muted-foreground leading-tight mt-1">{m.label}</p></CardContent></Card>
            )) : <Card className="col-span-full"><CardContent className="p-4 text-center text-sm text-muted-foreground">Loading live metrics…</CardContent></Card>}
          </div>

          {threatIntel && (
            <Card data-testid="threat-intel-card">
              <CardHeader className="pb-2 flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> Threat intelligence — {threatIntel.source}</CardTitle>
                <Badge variant="outline" className="text-[10px]">{threatIntel.total.toLocaleString()} KEV entries</Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {threatIntel.matches?.length > 0 && (
                  <div className="p-3 rounded-lg border border-severity-high/30 bg-severity-high/5" data-testid="kev-matches">
                    <p className="text-sm font-medium text-severity-high mb-2">{threatIntel.matches.length} actively exploited CVE(s) match technology named in your products</p>
                    <div className="space-y-1.5">{threatIntel.matches.slice(0, 8).map(v => <div key={v.cveID} className="text-xs flex flex-wrap gap-x-2"><span className="font-mono text-primary">{v.cveID}</span><span className="font-medium">{v.vendorProject} {v.product}</span><span className="text-muted-foreground truncate">— {v.vulnerabilityName}</span></div>)}</div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Recently added to CISA KEV (last 14 days)</p>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-1.5">{threatIntel.recent_additions.map(v => (
                    <div key={v.cveID} className="p-2 rounded-lg border bg-card text-xs flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-mono text-primary">{v.cveID}</span><span className="font-medium">{v.vendorProject} · {v.product}</span>
                      <span className="text-muted-foreground flex-1 min-w-[200px] truncate">{v.vulnerabilityName}</span>
                      {v.knownRansomwareCampaignUse === 'Known' && <Badge variant="outline" className="text-[9px] border-severity-critical/30 text-severity-critical">Ransomware</Badge>}
                      <span className="text-muted-foreground">{v.dateAdded}</span>
                    </div>
                  ))}</div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          <Card data-testid="soc-events">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Security Events</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[350px]">
                <div className="space-y-2">
                  {socEvents.map((event, i) => (
                    <div key={event.event_id || i} className="p-3 rounded-lg border bg-card flex items-center gap-3">
                      <div className={`p-2 rounded-lg shrink-0 ${severityColors[event.severity]?.bg || 'bg-muted'}`}>
                        <Activity className={`h-4 w-4 ${severityColors[event.severity]?.text || 'text-muted-foreground'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{event.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                        <p className="text-xs text-muted-foreground truncate">{event.description || `${event.source_ip} → ${event.destination_ip}`}</p>
                      </div>
                      <Badge variant="outline" className={`shrink-0 text-[10px] ${severityColors[event.severity]?.border || ''} ${severityColors[event.severity]?.text || ''}`}>
                        {event.severity}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground shrink-0">{new Date(event.timestamp).toLocaleTimeString()}</span>
                    </div>
                  ))}
                  {socEvents.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm"><Activity className="h-8 w-8 mx-auto mb-2 opacity-30" />No events yet. Logins, scans, credit denials and dark web exposures appear here in real time.</div>}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Threat Modelling Tab */}
        <TabsContent value="threats" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Threat Modelling</h2>
              <p className="text-sm text-muted-foreground">STRIDE, MITRE ATT&CK analysis and attack surface mapping</p>
            </div>
            <Dialog open={showThreatModelForm} onOpenChange={setShowThreatModelForm}>
              <DialogTrigger asChild>
                <Button data-testid="create-threat-model-btn"><Plus className="h-4 w-4 mr-2" /> New Threat Model</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create Threat Model</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-4">
                  <div><Label>Application Name</Label><Input value={threatModelForm.application_name} onChange={e => setThreatModelForm(p => ({ ...p, application_name: e.target.value }))} placeholder="e.g., Payment Gateway" /></div>
                  <div><Label>Application Type</Label>
                    <Select value={threatModelForm.application_type} onValueChange={v => setThreatModelForm(p => ({ ...p, application_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="web">Web Application</SelectItem>
                        <SelectItem value="api">API Service</SelectItem>
                        <SelectItem value="mobile">Mobile App</SelectItem>
                        <SelectItem value="iot">IoT Device</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Assets (comma-separated)</Label><Input value={threatModelForm.assets} onChange={e => setThreatModelForm(p => ({ ...p, assets: e.target.value }))} placeholder="e.g., Database, API Gateway, User Data" /></div>
                  <div><Label>Data Flows (comma-separated)</Label><Input value={threatModelForm.data_flows} onChange={e => setThreatModelForm(p => ({ ...p, data_flows: e.target.value }))} placeholder="e.g., User → API → Database" /></div>
                  <Button onClick={createThreatModel} className="w-full">Generate Threat Model</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {threatModels.map(model => (
              <Card key={model.model_id} data-testid={`threat-model-${model.model_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{model.application_name}</CardTitle>
                    <Badge variant="outline">{model.application_type}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-primary">{model.risk_score}</p>
                      <p className="text-[10px] text-muted-foreground">Risk Score</p>
                    </div>
                    <Separator orientation="vertical" className="h-10" />
                    <div className="text-center">
                      <p className="text-2xl font-bold">{model.stride_analysis?.length || 0}</p>
                      <p className="text-[10px] text-muted-foreground">STRIDE Threats</p>
                    </div>
                    <Separator orientation="vertical" className="h-10" />
                    <div className="text-center">
                      <p className="text-2xl font-bold">{model.mitre_mapping?.length || 0}</p>
                      <p className="text-[10px] text-muted-foreground">MITRE Tactics</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Top Recommendations:</p>
                    {model.recommendations?.slice(0, 3).map((rec, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <CheckCircle className="h-3 w-3 text-primary shrink-0" />
                        <span>{rec}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            {threatModels.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-12 text-center">
                  <Target className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">No threat models yet. Create one to analyze your attack surface.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Incident Response</h2>
              <p className="text-sm text-muted-foreground">Track, manage, and respond to security incidents</p>
            </div>
            <Dialog open={showIncidentForm} onOpenChange={setShowIncidentForm}>
              <DialogTrigger asChild>
                <Button data-testid="create-incident-btn"><Plus className="h-4 w-4 mr-2" /> Report Incident</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Report Security Incident</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-4">
                  <div><Label>Title</Label><Input value={incidentForm.title} onChange={e => setIncidentForm(p => ({ ...p, title: e.target.value }))} placeholder="Incident title" /></div>
                  <div><Label>Description</Label><Textarea value={incidentForm.description} onChange={e => setIncidentForm(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="Describe the incident..." /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Severity</Label>
                      <Select value={incidentForm.severity} onValueChange={v => setIncidentForm(p => ({ ...p, severity: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="critical">Critical</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Category</Label>
                      <Select value={incidentForm.category} onValueChange={v => setIncidentForm(p => ({ ...p, category: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="security_event">Security Event</SelectItem>
                          <SelectItem value="data_breach">Data Breach</SelectItem>
                          <SelectItem value="malware">Malware</SelectItem>
                          <SelectItem value="phishing">Phishing</SelectItem>
                          <SelectItem value="unauthorized_access">Unauthorized Access</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Affected Assets (comma-separated)</Label><Input value={incidentForm.affected_assets} onChange={e => setIncidentForm(p => ({ ...p, affected_assets: e.target.value }))} placeholder="e.g., web-server-01, database" /></div>
                  <Button onClick={createIncident} className="w-full">Create Incident</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {incidents.map(incident => (
                <Card key={incident.incident_id} data-testid={`incident-${incident.incident_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg shrink-0 ${severityColors[incident.severity]?.bg || 'bg-muted'}`}>
                        <FileWarning className={`h-5 w-5 ${severityColors[incident.severity]?.text || 'text-muted-foreground'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">{incident.title}</span>
                          <Badge variant="outline" className={`${severityColors[incident.severity]?.border || ''} ${severityColors[incident.severity]?.text || ''}`}>
                            {incident.severity}
                          </Badge>
                          <Badge variant="outline">{incident.status?.replace(/_/g, ' ')}</Badge>
                          {incident.is_demo && <Badge variant="outline" className="text-[10px]">Demo</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{incident.description || incident.category?.replace(/_/g, ' ')}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(incident.created_at).toLocaleString()}</span>
                          <span className="flex items-center gap-1"><Users className="h-3 w-3" />{incident.assigned_to}</span>
                          {incident.affected_assets?.length > 0 && (
                            <span className="flex items-center gap-1"><Server className="h-3 w-3" />{incident.affected_assets.length} assets</span>
                          )}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm"><ChevronRight className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {incidents.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CheckCircle className="h-10 w-10 mx-auto mb-3 text-green-500/30" />
                    <p className="text-sm text-muted-foreground">No active incidents. Your environment is secure!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}
